import { GamePlayScene } from '../scenes/GamePlayScene';
import { Earth } from '../entities/earth/Earth';
import { AudioManager, SoundEffect } from '../systems/AudioManager';
import { t } from '../i18n/lang';

export interface EarthUpgradeResult {
  success: boolean;
  cost: number;
  newCredits: number;
  newLevel: number;
}

export class EarthAbilityManager {
  private scene: GamePlayScene;
  private audioManager: AudioManager;
  
  // Ability effects
  private abilityEffects: Map<string, Phaser.GameObjects.Graphics> = new Map();

  constructor(scene: GamePlayScene) {
    this.scene = scene;
    this.audioManager = AudioManager.getInstance(scene);
  }

  tryActivateEarthAbility(earth: Earth | null): boolean {
    if (!earth) return false;
    
    // Check if ability is ready
    if (!earth.canUseSpecialAbility()) {
      this.showCooldownMessage(earth);
      return false;
    }
    
    // Activate ability
    const success = earth.triggerSpecialAbility();
    if (success) {
      this.showEarthAbilityEffect(earth);
      this.audioManager.playSFX(SoundEffect.EARTH_ABILITY);
    }
    
    return success;
  }

  activateEarthAbilityFallback(earth: Earth | null): void {
    if (!earth) return;
    
    const earthAny = earth as any;
    if (earthAny.activateAbility) {
      earthAny.activateAbility();
    } else {
      // Fallback implementation based on Earth type
      this.executeAbilityByType(earth);
    }
  }

  upgradeEarth(earth: Earth | null, energyCredits: number): EarthUpgradeResult {
    if (!earth) {
      return { success: false, cost: 0, newCredits: energyCredits, newLevel: 0 };
    }
    
    const currentLevel = earth.getLevel();
    const upgradeCost = this.calculateUpgradeCost(currentLevel);
    
    if (energyCredits < upgradeCost) {
      return { success: false, cost: upgradeCost, newCredits: energyCredits, newLevel: currentLevel };
    }
    
    // Upgrade Earth
    earth.upgrade();
    
    // Play upgrade sound
    this.audioManager.playSFX(SoundEffect.EARTH_UPGRADE);
    
    // Show upgrade effect
    this.showUpgradeEffect(earth);
    
    const newCredits = energyCredits - upgradeCost;
    const newLevel = earth.getLevel();
    
    return { success: true, cost: upgradeCost, newCredits, newLevel };
  }

  showEarthAbilityEffect(earth: Earth): void {
    const abilityName = earth.getSpecialAbility();
    
    switch (abilityName) {
      case 'Freeze':
        this.showFreezeEffect(earth);
        break;
      case 'Reflect':
        this.showReflectEffect(earth);
        break;
      case 'GenerateEnergy':
        this.showEnergyGenerationEffect(earth);
        break;
      case 'Teleport':
        this.showTeleportEffect(earth);
        break;
      case 'GravityField':
        this.showGravityFieldEffect(earth);
        break;
      default:
        this.showGenericAbilityEffect(earth);
    }
  }

  private showCooldownMessage(earth: Earth): void {
    const cooldownRemaining = earth.getAbilityCooldownRemaining();
    const message = `${t('ability_cooldown')}: ${Math.ceil(cooldownRemaining / 1000)}s`;
    
    const text = this.scene.add.text(
      earth.x,
      earth.y - 50,
      message,
      {
        fontSize: '16px',
        color: '#ff6b6b',
        fontFamily: 'Arial',
        backgroundColor: 'rgba(0,0,0,0.7)',
        padding: { x: 8, y: 4 }
      }
    ).setOrigin(0.5);
    
    this.scene.tweens.add({
      targets: text,
      y: text.y - 30,
      alpha: 0,
      duration: 1500,
      ease: 'Power2',
      onComplete: () => text.destroy()
    });
  }

  private calculateUpgradeCost(currentLevel: number): number {
    return 500 + (currentLevel - 1) * 200;
  }

  private executeAbilityByType(earth: Earth): void {
    const abilityName = earth.getSpecialAbility();
    
    switch (abilityName) {
      case 'Freeze':
        this.executeFreezeAbility(earth);
        break;
      case 'Reflect':
        this.executeReflectAbility(earth);
        break;
      case 'GenerateEnergy':
        this.executeEnergyGenerationAbility();
        break;
      case 'Teleport':
        this.executeTeleportAbility();
        break;
      case 'GravityField':
        this.executeGravityFieldAbility();
        break;
    }
  }

  private executeFreezeAbility(earth: Earth): void {
    const radius = 180;
    const freezeDuration = 2500;
    
    this.scene.children.list.forEach(child => {
      if (child && typeof (child as any).applyFreeze === 'function') {
        const distance = Phaser.Math.Distance.Between(earth.x, earth.y, (child as any).x, (child as any).y);
        if (distance <= radius) {
          (child as any).applyFreeze(freezeDuration);
        }
      }
    });
  }

  private executeReflectAbility(earth: Earth): void {
    // This is handled by VolcanicEarth internally
    // Just show visual effect
    this.showReflectEffect(earth);
  }

  private executeEnergyGenerationAbility(): void {
    const energyGain = 300;
    (this.scene as any).addEnergyCredits(energyGain);
  }

  private executeTeleportAbility(): void {
    // Find nearest tower and teleport it
    const towers = (this.scene as any).towers || [];
    if (towers.length === 0) return;
    
    let nearestTower = towers[0];
    let minDistance = Infinity;
    
    const earthPosX = this.scene.cameras.main.centerX;
    const earthPosY = this.scene.cameras.main.centerY;
    for (const tower of towers) {
      const distance = Phaser.Math.Distance.Between(earthPosX, earthPosY, tower.x, tower.y);
      if (distance < minDistance) {
        minDistance = distance;
        nearestTower = tower;
      }
    }
    
    // Teleport to random position
    const angle = Math.random() * Math.PI * 2;
    const distance = 120;
    nearestTower.x = earthPosX + Math.cos(angle) * distance;
    nearestTower.y = earthPosY + Math.sin(angle) * distance;
  }

  private executeGravityFieldAbility(): void {
    const slowAmount = 0.5;
    const slowDuration = 3500;
    
    this.scene.children.list.forEach(child => {
      if (child && typeof (child as any).applySlow === 'function') {
        (child as any).applySlow(slowAmount, slowDuration);
      }
    });
  }

  // Visual effects
  private showFreezeEffect(earth: Earth): void {
    const effect = this.scene.add.graphics();
    effect.lineStyle(4, 0x00ffff, 0.8);
    effect.strokeCircle(earth.x, earth.y, 180);
    
    this.scene.tweens.add({
      targets: effect,
      alpha: 0,
      scaleX: 1.2,
      scaleY: 1.2,
      duration: 2500,
      ease: 'Power2',
      onComplete: () => effect.destroy()
    });
    
    this.abilityEffects.set('freeze', effect);
  }

  private showReflectEffect(earth: Earth): void {
    const effect = this.scene.add.graphics();
    effect.lineStyle(6, 0xff4444, 0.9);
    effect.strokeCircle(earth.x, earth.y, 60);
    
    this.scene.tweens.add({
      targets: effect,
      alpha: 0,
      scaleX: 1.5,
      scaleY: 1.5,
      duration: 4000,
      ease: 'Power2',
      onComplete: () => effect.destroy()
    });
    
    this.abilityEffects.set('reflect', effect);
  }

  private showEnergyGenerationEffect(earth: Earth): void {
    // Create energy particles
    for (let i = 0; i < 8; i++) {
      const particle = this.scene.add.circle(
        earth.x,
        earth.y,
        3,
        0xffff00
      );
      
      const angle = (Math.PI * 2 * i) / 8;
      const targetX = earth.x + Math.cos(angle) * 100;
      const targetY = earth.y + Math.sin(angle) * 100;
      
      this.scene.tweens.add({
        targets: particle,
        x: targetX,
        y: targetY,
        alpha: 0,
        duration: 1000,
        ease: 'Power2',
        onComplete: () => particle.destroy()
      });
    }
  }

  private showTeleportEffect(earth: Earth): void {
    const effect = this.scene.add.graphics();
    effect.lineStyle(5, 0xff00ff, 0.8);
    effect.strokeCircle(earth.x, earth.y, 80);
    
    this.scene.tweens.add({
      targets: effect,
      alpha: 0,
      rotation: Math.PI * 2,
      scaleX: 0.5,
      scaleY: 0.5,
      duration: 1000,
      ease: 'Power2',
      onComplete: () => effect.destroy()
    });
  }

  private showGravityFieldEffect(earth: Earth): void {
    const effect = this.scene.add.graphics();
    effect.lineStyle(8, 0x8800ff, 0.7);
    
    // Create pulsing gravity field
    for (let r = 50; r <= 200; r += 50) {
      effect.strokeCircle(earth.x, earth.y, r);
    }
    
    this.scene.tweens.add({
      targets: effect,
      alpha: 0,
      duration: 3500,
      ease: 'Power2',
      onComplete: () => effect.destroy()
    });
  }

  private showGenericAbilityEffect(earth: Earth): void {
    const effect = this.scene.add.graphics();
    effect.lineStyle(6, 0x00ff00, 0.8);
    effect.strokeCircle(earth.x, earth.y, 60);
    
    this.scene.tweens.add({
      targets: effect,
      alpha: 0,
      scaleX: 1.5,
      scaleY: 1.5,
      duration: 1000,
      ease: 'Power2',
      onComplete: () => effect.destroy()
    });
  }

  private showUpgradeEffect(earth: Earth): void {
    // Create upgrade particles
    for (let i = 0; i < 12; i++) {
      const particle = this.scene.add.circle(
        earth.x + (Math.random() - 0.5) * 40,
        earth.y + (Math.random() - 0.5) * 40,
        4,
        0x00ff00
      );
      
      this.scene.tweens.add({
        targets: particle,
        y: particle.y - 80,
        alpha: 0,
        scaleX: 0.1,
        scaleY: 0.1,
        duration: 1500,
        ease: 'Power2',
        delay: i * 100,
        onComplete: () => particle.destroy()
      });
    }
    
    // Show level up text
    const levelText = this.scene.add.text(
      earth.x,
      earth.y - 60,
      `Level ${earth.getLevel()}!`,
      {
        fontSize: '20px',
        color: '#00ff00',
        fontFamily: 'Arial',
        fontStyle: 'bold'
      }
    ).setOrigin(0.5);
    
    this.scene.tweens.add({
      targets: levelText,
      y: levelText.y - 40,
      alpha: 0,
      scaleX: 1.2,
      scaleY: 1.2,
      duration: 2000,
      ease: 'Power2',
      onComplete: () => levelText.destroy()
    });
  }

  // Helper methods
  getEarthAbilityName(earth: Earth | null): string {
    return earth ? earth.getSpecialAbility() : 'None';
  }

  getEarthLevel(earth: Earth | null): number {
    return earth ? earth.getLevel() : 0;
  }

  getEarthDamageResistance(earth: Earth | null): number {
    return earth ? earth.getDamageResistance() : 0;
  }

  isAbilityReady(earth: Earth | null): boolean {
    return earth ? earth.canUseSpecialAbility() : false;
  }

  getAbilityCooldownRemaining(earth: Earth | null): number {
    return earth ? earth.getAbilityCooldownRemaining() : 0;
  }
} 